import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  Box,
  Autocomplete,
  TableContainer,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus, IconCheck, IconX } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";

// Lista de status de contratos disponíveis
const contractStatusOptions = [
  { label: 'AJUIZAR', value: 'AJUIZAR' },
  { label: 'NÃO POSSUI ABUSIVIDADE', value: 'NAO_POSSUI_ABUSIVIDADE' },
  { label: 'FALTAM DOCUMENTOS', value: 'FALTAM_DOCUMENTOS' },
  { label: 'CANCELADO', value: 'CANCELADO' },
  { label: 'PROTOCOLO URGENTE', value: 'PROTOCOLO_URGENTE' },
  { label: 'PROTOCOLADO', value: 'PROTOCOLADO' },
  { label: 'TRÂNSITO EM JULGADO', value: 'TRANSITO_EM_JULGADO' },
  { label: 'REAJUIZAR', value: 'REAJUIZAR' },
  { label: 'PASTA FEITA', value: 'PASTA_FEITA' },
  { label: 'DESATUALIZADO', value: 'DESATUALIZADO' },
];

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const UserProfilesList = () => {
  const theme = useTheme();
  const stickyCellBg = theme.palette.background.paper;
  const stickyActionsSx = {
    position: "sticky",
    right: 0,
    backgroundColor: stickyCellBg,
    minWidth: 100,
    boxShadow: "-4px 0 8px -4px rgba(0,0,0,0.15)",
  };
  const stickyNameSx = {
    position: "sticky",
    left: 0,
    backgroundColor: stickyCellBg,
    minWidth: 160,
    boxShadow: "4px 0 8px -4px rgba(0,0,0,0.15)",
  };

  const [profiles, setProfiles] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProfile, setCurrentProfile] = useState({
    name: "",
    description: "",
    can_create_contracts: false,
    can_edit_contracts: false,
    can_delete_contracts: false,
    can_restore_contracts: false,
    can_file_contracts: false,
    can_manage_users: false,
    can_import_data: false,
    can_view_commission_reports: false,
    can_view_attendances: false,
    can_create_attendances: false,
    can_add_attendance_interactions: false,
    allow_contracts_without_calc_fields: false,
    allowed_contract_statuses: "",
    is_active: true,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fetchProfiles = useCallback(() => {
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/user-profiles/?page=${page + 1}&page_size=${rowsPerPage}${searchText}`
    )
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Não foi possível carregar a lista de perfis. Tente novamente.";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setProfiles(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao carregar perfis:", error);
        alert(error.message);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (profile) => {
    setCurrentProfile(profile);
    setIsEditing(true);
    setOpenModal(true);
    setFieldErrors({});
  };

  const handleAdd = () => {
    setCurrentProfile({
      name: "",
      description: "",
    can_create_contracts: false,
    can_edit_contracts: false,
    can_delete_contracts: false,
    can_restore_contracts: false,
    can_file_contracts: false,
    can_manage_users: false,
    can_import_data: false,
    can_view_commission_reports: false,
    can_view_attendances: false,
    can_create_attendances: false,
    can_add_attendance_interactions: false,
    allow_contracts_without_calc_fields: false,
    allowed_contract_statuses: "",
    is_active: true,
    });
    setIsEditing(false);
    setOpenModal(true);
    setFieldErrors({});
  };

  // Converte a string de status para array de objetos selecionados
  const getSelectedStatuses = () => {
    if (!currentProfile.allowed_contract_statuses) return [];
    const values = currentProfile.allowed_contract_statuses.split(',').map(s => s.trim()).filter(s => s);
    return contractStatusOptions.filter(option => values.includes(option.value));
  };

  // Converte array de objetos selecionados para string
  const handleStatusChange = (event, newValue) => {
    const statusString = newValue.map(item => item.value).join(',');
    setCurrentProfile({ ...currentProfile, allowed_contract_statuses: statusString });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFieldErrors({});
  };

  const handleCloseWithConfirmation = () => {
    if (window.confirm("Deseja realmente fechar? Todas as alterações não salvas serão descartadas.")) {
      setOpenModal(false);
    }
  };

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/user-profiles/${currentProfile.id}/`
      : "/backend/user-profiles/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentProfile),
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          const fieldMapping = {
            name: 'name',
            description: 'description',
          };
          
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField] && Array.isArray(errorData[backendField])) {
              errors[fieldMapping[backendField]] = errorData[backendField][0];
            }
          });
          
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          if (generalError) {
            throw new Error(generalError);
          } else if (Object.keys(errors).length > 0) {
            throw new Error("Por favor, corrija os erros nos campos destacados.");
          } else {
            throw new Error("Não foi possível salvar o perfil. Verifique os dados e tente novamente.");
          }
        } catch (e) {
          if (e.message) {
            throw e;
          }
          console.warn("Não foi possível extrair mensagem de erro específica:", e);
          throw new Error("Não foi possível salvar o perfil. Verifique os dados e tente novamente.");
        }
      })
      .then((data) => {
        if (isEditing) {
          setProfiles((prevProfiles) =>
            prevProfiles.map((profile) => (profile.id === data.id ? data : profile))
          );
        } else {
          setProfiles((prevProfiles) => [...prevProfiles, data]);
        }
        alert("Perfil salvo com sucesso!");
        setOpenModal(false);
        setFieldErrors({});
        fetchProfiles();
      })
      .catch((error) => {
        console.error("Erro ao salvar perfil:", error);
        alert(error.message);
      });
  };

  const handleDelete = (profileId) => {
    if (window.confirm("Deseja realmente excluir este perfil? Esta ação não pode ser desfeita.")) {
      fetch(`/backend/user-profiles/${profileId}/`, {
        method: "DELETE",
      })
        .then(async (response) => {
          if (response.ok) {
            setProfiles((prevProfiles) =>
              prevProfiles.filter((profile) => profile.id !== profileId)
            );
            alert("Perfil removido com sucesso!");
            fetchProfiles();
          } else {
            let errorMessage = "Não foi possível excluir o perfil. Verifique se não há usuários vinculados.";
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.detail) {
                errorMessage = errorData.detail;
              }
            } catch (e) {
              console.warn("Não foi possível extrair mensagem de erro específica:", e);
            }
            throw new Error(errorMessage);
          }
        })
        .catch((error) => {
          console.error("Erro ao excluir perfil:", error);
          alert(error.message);
        });
    }
  };

  const renderPermissionIcon = (hasPermission) => {
    return hasPermission ? (
      <IconCheck stroke={2} size="1.2rem" color="green" />
    ) : (
      <IconX stroke={2} size="1.2rem" color="red" />
    );
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de Perfis de Usuário"
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar perfil
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <TableContainer sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1600 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...stickyNameSx, zIndex: 2 }}>Nome</TableCell>
              <TableCell align="center">Incluir Contratos</TableCell>
              <TableCell align="center">Alterar Contratos</TableCell>
              <TableCell align="center">Excluir Contratos</TableCell>
              <TableCell align="center">Restaurar Contratos</TableCell>
              <TableCell align="center">Ajuizar Contratos</TableCell>
              <TableCell align="center">Gerenciar Usuários</TableCell>
              <TableCell align="center">Importar Dados</TableCell>
              <TableCell align="center">Relatório Comissões</TableCell>
              <TableCell align="center">Ver Atendimentos</TableCell>
              <TableCell align="center">Cadastrar Atendimentos</TableCell>
              <TableCell align="center">Interações Atendimento</TableCell>
              <TableCell align="center">Sem Campos de Cálculo</TableCell>
              <TableCell align="center">Status Visíveis</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Usuários</TableCell>
              <TableCell align="right" sx={{ ...stickyActionsSx, zIndex: 2 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map((profile) => (
              <TableRow key={profile.id}>
                <TableCell sx={{ ...stickyNameSx, zIndex: 1 }}>
                  <Box>
                    <strong>{profile.name}</strong>
                    {profile.description && (
                      <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
                        {profile.description}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">{renderPermissionIcon(profile.can_create_contracts)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(profile.can_edit_contracts)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(profile.can_delete_contracts)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Permite visualizar a lista de contratos excluídos e restaurá-los">
                    <span>{renderPermissionIcon(profile.can_restore_contracts)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">{renderPermissionIcon(profile.can_file_contracts)}</TableCell>
                <TableCell align="center">{renderPermissionIcon(profile.can_manage_users)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Permite importar dados via CSV (contratos, clientes e profissões)">
                    <span>{renderPermissionIcon(profile.can_import_data)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Permite acessar o menu Relatórios > Comissões dos consultores">
                    <span>{renderPermissionIcon(profile.can_view_commission_reports)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Permite acessar o menu Atendimento e consultar registros">
                    <span>{renderPermissionIcon(profile.can_view_attendances)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Permite abrir novos atendimentos">
                    <span>{renderPermissionIcon(profile.can_create_attendances)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Permite registrar novas interações em um atendimento existente">
                    <span>{renderPermissionIcon(profile.can_add_attendance_interactions)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Permite incluir/alterar contratos sem preencher os campos de cálculo da inicial">
                    <span>{renderPermissionIcon(profile.allow_contracts_without_calc_fields)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  {profile.allowed_statuses_display && profile.allowed_statuses_display.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {profile.allowed_statuses_display.map((status, idx) => (
                        <Chip 
                          key={idx}
                          label={status} 
                          size="small"
                          variant="outlined"
                          color={status === "Todos os status" ? "default" : "info"}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Chip label="Todos" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={profile.is_active ? "Ativo" : "Inativo"} 
                    color={profile.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={profile.users_count || 0} 
                    color="primary" 
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right" sx={{ ...stickyActionsSx, zIndex: 1 }}>
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(profile)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(profile.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Perfis por página"
      />

      <Dialog 
        open={openModal} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          if (reason === 'escapeKeyDown') {
            handleCloseWithConfirmation();
            return;
          }
          handleCloseModal();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? "Editar Perfil" : "Adicionar Perfil"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                label="Nome do Perfil"
                value={currentProfile.name}
                onChange={(e) => {
                  setCurrentProfile({ ...currentProfile, name: e.target.value });
                  if (fieldErrors.name) {
                    setFieldErrors({ ...fieldErrors, name: null });
                  }
                }}
                fullWidth
                sx={{ mt: 2 }}
                autoComplete="off"
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || ""}
                required
              />
            </Grid>

            {/* Descrição */}
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                value={currentProfile.description || ""}
                onChange={(e) => {
                  setCurrentProfile({ ...currentProfile, description: e.target.value });
                  if (fieldErrors.description) {
                    setFieldErrors({ ...fieldErrors, description: null });
                  }
                }}
                fullWidth
                multiline
                rows={2}
                autoComplete="off"
                error={!!fieldErrors.description}
                helperText={fieldErrors.description || "Descreva as responsabilidades deste perfil"}
              />
            </Grid>

            {/* Permissões de Contratos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>Permissões de Contratos</Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_create_contracts}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_create_contracts: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite incluir contratos"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_edit_contracts}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_edit_contracts: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite alterar contratos"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_delete_contracts}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_delete_contracts: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite excluir contratos"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_restore_contracts || false}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_restore_contracts: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite restaurar contratos excluídos"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_file_contracts}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_file_contracts: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite ajuizar contratos"
              />
            </Grid>

            {/* Permissões de Usuários */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>Permissões de Usuários</Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_manage_users}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_manage_users: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite gerenciar usuários (acesso ao menu Gestão de Usuários)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_import_data || false}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_import_data: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite importar dados via CSV (contratos, clientes e profissões)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_view_commission_reports || false}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_view_commission_reports: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite visualizar relatório de comissões (acesso ao menu Relatórios > Comissões)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>Permissões de Atendimento</Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_view_attendances || false}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_view_attendances: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite visualizar atendimentos"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_create_attendances || false}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_create_attendances: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite cadastrar atendimentos"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.can_add_attendance_interactions || false}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, can_add_attendance_interactions: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite adicionar interações em atendimentos"
              />
            </Grid>

            {/* Permissões Especiais de Contratos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>Permissões Especiais</Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.allow_contracts_without_calc_fields || false}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, allow_contracts_without_calc_fields: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permite salvar contratos sem campos de cálculo (inclui/altera contratos sem informar valor contratado, parcelas, taxa de juros, datas, etc.)"
              />
            </Grid>

            {/* Filtro de Status */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>Filtro de Visualização</Box>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={contractStatusOptions}
                getOptionLabel={(option) => option.label}
                value={getSelectedStatuses()}
                onChange={handleStatusChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status de Contratos Visíveis"
                    placeholder="Selecione os status..."
                    helperText="Deixe em branco para permitir visualizar todos os status"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.label}
                      {...getTagProps({ index })}
                      size="small"
                      color="info"
                    />
                  ))
                }
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentProfile.is_active}
                    onChange={(e) => setCurrentProfile({ ...currentProfile, is_active: e.target.checked })}
                    color="primary"
                  />
                }
                label="Perfil ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserProfilesList;

